import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '#ui/dialog.js';
import { DetailRow } from '#ui/itinerary/ItineraryDetailRow.js';

/** One booked room to choose from. */
export interface BookingChoice {
  id: string;
  title: string;
  /** "Booked", "Pending", "Cancelling" — the status label the bookings tab shows. */
  statusLabel: string;
  /** Tailwind text colour for the status, matching the bookings tab. */
  statusClassName?: string;
  confirmationNumber?: string;
}

/**
 * Picks which booking to open when a rate is booked more than once: the Booked button lands
 * here when the rate is booked more than once; a single booking skips it. Mobile asks the same question
 * with an action sheet. Presentational, so its open state can be shown in Storybook.
 */
export function BookingChooserDialog({
  open,
  onOpenChange,
  bookings,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookings: BookingChoice[];
  onSelect: (id: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Your bookings</DialogTitle>
          <DialogDescription>Choose a room to see its booking details.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          {bookings.map((booking, index) => (
            <DetailRow
              key={booking.id}
              icon="confirmation_number"
              value={booking.title || `Room ${index + 1}`}
              secondary={
                <>
                  <span className={`font-medium ${booking.statusClassName ?? ''}`}>{booking.statusLabel}</span>
                  {booking.confirmationNumber ? ` · Confirmation #${booking.confirmationNumber}` : ''}
                </>
              }
              onClick={() => onSelect(booking.id)}
              // The hover fills the row out to the dialog's edges: its 24px padding, matched in the row.
              className="-mx-6 px-6"
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
